<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ElUpload } from 'element-plus'
import { UploadFilled } from '@element-plus/icons-vue'
import type { UploadProps, UploadUserFile } from 'element-plus'

import { fileRulesTip, isAcceptedFile } from '@/shared/lib'

/**
 * Several documents picked for one form field — a company's registration papers, where the
 * set is sent in a single request and means nothing apart. `FileUpload` and not this for a
 * single document; the checks and their wording are the same, and live in `@/shared/lib`.
 *
 * Nothing is uploaded here either: the `File`s go into `v-model`.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: File[]
    /** Extensions with the dot, comma-separated: `.pdf` or `.pdf,.jpg`. */
    accept: string
    maxSizeMb: number
    /** How many the form takes at once. */
    limit?: number
    tip?: string
    block?: boolean
  }>(),
  { modelValue: () => [], limit: 10, tip: '', block: false }
)

const emit = defineEmits<{
  (e: 'update:modelValue', files: File[]): void
}>()

const fileList = ref<UploadUserFile[]>([])

const shownTip = computed(
  () =>
    props.tip ||
    fileRulesTip({ accept: props.accept, maxSizeMb: props.maxSizeMb })
)

/**
 * The list belongs to `ElUpload` — rebuilding it here would lose the `uid` it keys removals
 * by. The watch only answers a reset from the form (a modal that closed and reopened).
 */
watch(
  () => props.modelValue,
  (files) => {
    if (files.length === 0) fileList.value = []
  }
)

function emitFiles() {
  const files: File[] = []

  for (const item of fileList.value) if (item.raw) files.push(item.raw)

  emit('update:modelValue', files)
}

const handleChange: UploadProps['onChange'] = (file, list) => {
  const rules = { accept: props.accept, maxSizeMb: props.maxSizeMb }

  // `ElUpload` has already appended it; a file that fails the check is taken back out.
  fileList.value =
    file.raw && !isAcceptedFile(file.raw, rules)
      ? list.filter((item) => item.uid !== file.uid)
      : [...list]

  emitFiles()
}

const handleRemove: UploadProps['onRemove'] = (_file, list) => {
  fileList.value = [...list]
  emitFiles()
}
</script>

<template>
  <ElUpload
    v-model:file-list="fileList"
    class="shared-files-upload"
    :class="{ 'shared-files-upload--block': block }"
    drag
    multiple
    :accept="accept"
    :auto-upload="false"
    :limit="limit"
    :on-change="handleChange"
    :on-remove="handleRemove"
    action="#"
  >
    <el-icon class="el-icon--upload"><UploadFilled /></el-icon>
    <div class="el-upload__text">
      Drop files here or <em>click to upload</em>
    </div>
    <template #tip>
      <div class="el-upload__tip">{{ shownTip }}</div>
    </template>
  </ElUpload>
</template>

<style lang="scss" scoped>
.shared-files-upload--block {
  width: 100%;
}
</style>
